import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/reducers";
import { useCallback } from "react";
import { fetchToken } from "store/user/thunks";
import { removeToken } from "store/user/slice";
import { AppDispatch } from "store/store";
import { setErrorNotification } from "store/errors/slice";
import { ErrorType } from "store/errors/types";
import SyncClient from "twilio-sync";
import { resetRoom } from "store/room/thunks";
import { setKnockingList, setKnockingUser } from "store/sync/slice";

export function useSyncClientLobby(syncClient?: SyncClient) {
  const dispatch = useDispatch<AppDispatch>();
  const { username } = useSelector((s: RootState) => s.user);

  const joinSyncRoom = useCallback(
    async (roomCode: string) => {
      if (syncClient) {
        const guestMap = await syncClient.map(roomCode + "-guests");
        const knockingList = await syncClient.list(roomCode + "-knocking");
        dispatch(setKnockingList(knockingList));
        const guests = await guestMap.getItems();
        const knocking = await knockingList.getItems({ from: 0, order: "asc" });
        const userInRoom = guests.items.find(
          (item: any) => item.data.name === username
        );
        const userWaiting = knocking.items.find(
          (item: any) => item.data.name === username
        );

        if (userInRoom || userWaiting) {
          throw new Error(`${username} is already in the room.`);
        }

        const knockingUserData = {
          name: username,
          status: "waiting",
          ping: Date.now(),
        };

        const knockingUser = await knockingList.push(knockingUserData);
        dispatch(setKnockingUser(knockingUser));

        knockingList.on("itemUpdated", async (event) => {
          if (event.item.data.name === knockingUserData.name) {
            if (event.item.data.lastUpdatedBy === "self") {
              return;
            }
            
            if (event.item.data.status === "waiting") {
              dispatch(removeToken());
              dispatch(resetRoom());
            }

            if (event.item.data.status === "rejected") {
              dispatch(removeToken());
              await knockingList.remove(knockingUser.index).catch((e) => {
                // Eat the "resource not found" error
                if (e.code !== 54201) {
                  throw e;
                }
              });
              window.location.reload();
            }

            if (event.item.data.status === "admitted") {
              dispatch(
                fetchToken({
                  Identity: username,
                  Room: roomCode,
                })
              ).catch((err: any) => {
                dispatch(
                  setErrorNotification({
                    name: err.name,
                    type: ErrorType.ConnectionError,
                    message: err.message,
                  })
                );
              });
            }
          }
        });

        window.addEventListener("beforeunload", function () {
          knockingList.remove(knockingUser.index);
        });
      }
    },
    [dispatch, syncClient, username]
  );

  return {
    joinSyncRoom,
  };
}
